import React, {useState, useEffect} from 'react';
import Carousel from './Carousel';
import Tabs from './Tabs';
import StateTabs from './StateTabs';
import closeIcon from '../../assets/images/gallery/icons/x.svg'
import ImageGrid from './ImageGrid';
import BeforeAfterView from './BeforeAfterView';
import { Helmet } from "react-helmet";
import {
  FOLDER_MAP,
  PROJECT_META,
  PROJECT_TAB_LABELS,
  type ProjectGallerySlug,
  type LegacyGalleryFolder,
} from '../../data/projectGallery';
import { CONSTRUCTION_SITE_PHOTOS } from '../../data/constructionSitePhotos';

function importAll(r: __WebpackModuleApi.RequireContext): string[] {
  return r.keys().map(key => r(key) as string);
}

const galleryByLegacy: Record<LegacyGalleryFolder, string[]> = {
  kitchens: importAll(require.context('../../assets/images/gallery/kitchens', true, /\.webp$/)),
  bathrooms: importAll(require.context('../../assets/images/gallery/bathrooms', true, /\.webp$/)),
  "full home": importAll(require.context('../../assets/images/gallery/fullhome', true, /\.webp$/)),
  extensions: importAll(require.context('../../assets/images/gallery/extensions', true, /\.webp$/)),
  outdoors: importAll(require.context('../../assets/images/gallery/outdoors', true, /\.webp$/)),
  roofing: importAll(require.context('../../assets/images/gallery/roofing', true, /\.webp$/)),
};

const beforeAfterByLegacy: Record<LegacyGalleryFolder, string[]> = {
  kitchens: importAll(require.context('../../assets/images/gallery/beforeAfter/kitchens', true, /\.webp$/)),
  bathrooms: importAll(require.context('../../assets/images/gallery/beforeAfter/bathrooms', true, /\.webp$/)),
  "full home": importAll(require.context('../../assets/images/gallery/beforeAfter/fullhome', true, /\.webp$/)),
  extensions: importAll(require.context('../../assets/images/gallery/beforeAfter/extensions', true, /\.webp$/)),
  outdoors: importAll(require.context('../../assets/images/gallery/beforeAfter/outdoors', true, /\.webp$/)),
  roofing: importAll(require.context('../../assets/images/gallery/beforeAfter/roofing', true, /\.webp$/)),
};

interface GalleryProps {
  initialGallery: ProjectGallerySlug;
}

function Gallery({initialGallery}: GalleryProps) {
  const [currentGallery, setCurrentGallery] = useState<string[]>([]);
  const [currentBeforeAfter, setCurrentBeforeAfter] = useState<string[]>([]);
  const [displaySlider, setDisplaySlider] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [carouselImages, setCarouselImages] = useState<string[]>([]);
  const [isBeforeAndAfterView, setIsBeforeAndAfterView] = useState(false);

  const isConstruction = initialGallery === 'construction';
  const meta = PROJECT_META[initialGallery];

  const openSliderWithImages = (imagesToDisplay: string[], index: number) => {
    setCarouselIndex(index);
    setCarouselImages(imagesToDisplay);
    setDisplaySlider(true);
  }

  const closeSlider = () => {
    setDisplaySlider(false);
  }

  useEffect(() => {
    if (isConstruction) {
      setCurrentGallery(CONSTRUCTION_SITE_PHOTOS);
      setCurrentBeforeAfter([]);
      setIsBeforeAndAfterView(false);
    } else {
      const legacyFolder = FOLDER_MAP[initialGallery as Exclude<ProjectGallerySlug, 'construction'>];
      const ba = beforeAfterByLegacy[legacyFolder];
      setCurrentGallery(galleryByLegacy[legacyFolder]);
      setCurrentBeforeAfter(ba);
      if (ba.length === 0) {
        setIsBeforeAndAfterView(false);
      }
    }
  }, [initialGallery, isConstruction]);

  const photoAlt = `${meta.title} — ${PROJECT_TAB_LABELS[initialGallery]}`;

  return (
    <main className="bg-white">
      <Helmet>
        <title>{meta.title} | Projects | MA Renovations</title>
        <meta
          name="description"
          content={`${meta.title}: ${meta.sector} work in ${meta.location}. ${meta.scope}. MA Renovations.`}
        />
      </Helmet>

      {displaySlider && (
        <section className="fixed top-24 bg-black/80 w-full h-[calc(100vh-6rem)] flex justify-center items-start pt-4 z-50">
          <div className="flex-col">
            <button className="cursor-default w-full flex justify-end py-2" onClick={closeSlider}>
              <img className="cursor-pointer h-12" src={closeIcon} alt="close"/>
            </button>
            <Carousel images={carouselImages} index={carouselIndex}/>
          </div>
        </section>
      )}

      <div className="pt-36 sm:pt-40 pb-6 px-6 max-w-5xl mx-auto">
        <h1 className="font-display text-3xl sm:text-4xl text-navy font-bold tracking-tight">{meta.title}</h1>
        <p className="mt-2 text-slate-500">{meta.sector} &middot; {meta.location} &middot; {meta.scope}</p>
      </div>

      <Tabs
        gallerySwitch={!isBeforeAndAfterView}
        setGallery={() => setIsBeforeAndAfterView(false)}
        setBeforeAndAfter={() => { if (currentBeforeAfter.length > 0) setIsBeforeAndAfterView(true); }}
        hasBeforeAfter={currentBeforeAfter.length > 0}
      />

      <section className="bg-white pb-12">
        <StateTabs />

        {isBeforeAndAfterView && currentBeforeAfter.length > 0 ? (
          <BeforeAfterView images={currentBeforeAfter} onImageClick={openSliderWithImages} altDescription={photoAlt}/>
        ) : (
          <ImageGrid images={currentGallery} onImageClick={openSliderWithImages} altDescription={photoAlt}/>
        )}
      </section>
    </main>
  )
}

export default Gallery;
